import { useRef, useEffect, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { v4 as uuidv4 } from 'uuid';
import log, { ROARR } from 'roarr';
import moment from 'moment';

import LogApi from '../apis/LogApi';
import { getIcError } from '../providers/utils';
/**
 * Custom hook to activate "Roarr" logs and catch no render errors and log them to splunk by calling POST v1/jslogs.
 * This hook will display an error notification as well with an UUID that can be used to identify
 * and find the log easily in splunk.
 */
export const useErrorLogger = () => {
  const logRef = useRef({});
  const notify = useNotify();

  const configRoarrLog = useCallback(() => {
    ROARR.write = message => {
      try {
        const payload = JSON.parse(message);
        if (
          logRef.current.message !== payload.message ||
          logRef.current.stacktrace !== payload.context.stacktrace
        ) {
          // Log Levels: 10 TRACE, 20	DEBUG, 30 INFO, 40 WARN, 50	ERROR, 60 FATAL
          const level =
            logLevel.find(x => x.id === payload.context.logLevel) || {};
          if (payload.context.logLevel >= 50) {
            console.error(payload);
            const logId = uuidv4();
            LogApi.sendClientlogs([
              {
                logId,
                level: level.name,
                message: payload.message,
                time: moment(payload.time).toISOString(),
                stacktrace: payload.context.stacktrace,
                location: window.location?.href,
                tags: payload.context.tags
              }
            ]).catch(err => {
              console.error('Server error: ' + getIcError(err));
            });
            notify('ra.log.error', {
              type: 'error',
              messageArgs: {
                logId: logId,
                message: payload.message
              }
            });
          } else if (payload.context.logLevel === 40) {
            console.warn(payload);
          } else {
            console.log(payload);
          }
          // Set current log information to the logRef so we can avoid logging the same error more the once
          logRef.current = {
            message: payload.message,
            stacktrace: payload.context.stacktrace
          };
        }
      } catch (error) {
        console.error('Error occurred while executing "Roarr" logs: ', error);
      }
    };
  }, [notify]);

  const onError = useCallback(() => {
    window.onerror = function(message, src, line, col, error) {
      // Avoid logging the render errors since it's handled with React Error Boundaries (look into SolsticLayout.js -> componentDidCatch) and it has better stack info
      if (error.stack && !error.stack.includes('at render')) {
        // Log the error with "Roarr" => sending the log to Splunk by calling POST v1/jslogs
        log.error(
          { ...error, stacktrace: error.stack, tags: 'Events Error' },
          `${error.message}`
        );
      }
    };
  }, []);

  useEffect(() => {
    configRoarrLog();
    onError();
  }, [onError, configRoarrLog]);

  return {};
};

const logLevel = [
  { id: 10, name: 'TRACE' },
  { id: 20, name: 'DEBUG' },
  { id: 30, name: 'INFO' },
  { id: 40, name: 'WARN' },
  { id: 50, name: 'ERROR' },
  { id: 60, name: 'FATAL' }
];
